import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MarketingTools = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const socialTemplates = [
    {
      id: 1,
      platform: 'WhatsApp',
      icon: 'MessageCircle',
      color: 'bg-green-500',
      template: `¡Hola! 👋\n\n¿Te gustaría ahorrar dinero en tus compras diarias?\n\nÚnete a BIZZNET y obtén descuentos exclusivos en cientos de negocios locales. 🎉\n\nRegistrate con mi enlace y comienza a ahorrar hoy:\n[TU_ENLACE]\n\n✨ Beneficios:\n• Descuentos exclusivos\n• Acceso a negocios verificados\n• Sistema de recompensas\n\n¡No te lo pierdas!`
    },
    {
      id: 2,
      platform: 'Facebook',
      icon: 'Facebook',
      color: 'bg-blue-600',
      template: `🎯 ¡Descubre BIZZNET! 🎯\n\nLa plataforma que conecta a consumidores inteligentes con los mejores negocios locales.\n\n💰 Obtén descuentos exclusivos\n🏪 Apoya negocios locales\n🎁 Gana recompensas por referir amigos\n\nÚnete ahora: [TU_ENLACE]\n\n#BIZZNET #Descuentos #AhorroInteligente #NegociosLocales`
    },
    {
      id: 3,
      platform: 'Instagram',
      icon: 'Instagram',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
      template: `✨ BIZZNET ✨\n\nTu pasaporte a descuentos exclusivos 🎫\n\n🛍️ Compra inteligente\n💎 Ahorra más\n🌟 Gana recompensas\n\nLink en bio 👆\n\n#BIZZNET #Descuentos #Shopping #AhorroInteligente #Colombia`
    },
    {
      id: 4,
      platform: 'TikTok',
      icon: 'Music',
      color: 'bg-black',
      template: `🎵 ¿Quieres ahorrar en grande? 💰\n\n✨ BIZZNET te conecta con descuentos exclusivos en negocios locales\n\n🔥 Beneficios:\n✅ Descuentos reales\n✅ Negocios verificados\n✅ Gana por referir\n\nRegistrate con mi link 👇\n[TU_ENLACE]\n\n#BIZZNET #Descuentos #Ahorro #TikTokColombia #NegociosLocales`
    }
  ];

  const marketingTips = [
    {
      icon: 'Target',
      title: 'Identifica tu Audiencia',
      description: 'Comparte con personas que valoran el ahorro y apoyan negocios locales'
    },
    {
      icon: 'MessageSquare',
      title: 'Personaliza tu Mensaje',
      description: 'Adapta las plantillas a tu estilo y audiencia para mayor autenticidad'
    },
    {
      icon: 'TrendingUp',
      title: 'Comparte Regularmente',
      description: 'Publica contenido sobre BIZZNET 2-3 veces por semana para mejor alcance'
    },
    {
      icon: 'Users',
      title: 'Construye Confianza',
      description: 'Comparte tu experiencia personal y los beneficios que has obtenido'
    },
    {
      icon: 'Zap',
      title: 'Usa Llamados a la Acción',
      description: 'Incluye siempre tu enlace de referido y motiva a la acción inmediata'
    },
    {
      icon: 'BarChart',
      title: 'Monitorea Resultados',
      description: 'Revisa tus estadísticas para identificar qué estrategias funcionan mejor'
    }
  ];

  const handleCopyTemplate = (template) => {
    navigator.clipboard?.writeText(template);
    setSelectedTemplate(template);
    setTimeout(() => setSelectedTemplate(null), 2000);
  };

  return (
    <div className="bg-card rounded-lg shadow-card p-6 border border-border">
      <h2 className="text-2xl font-bold text-foreground mb-6">Herramientas de Marketing</h2>
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} />
          Plantillas para Redes Sociales
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {socialTemplates?.map((item) => (
            <div key={item?.id} className="bg-muted/30 rounded-lg border border-border overflow-hidden">
              <div className={`${item?.color} p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <Icon name={item?.icon} size={24} color="white" />
                  <span className="text-white font-semibold">{item?.platform}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopyTemplate(item?.template)}
                  iconName={selectedTemplate === item?.template ? 'Check' : 'Copy'}
                  className="text-white hover:bg-white/20"
                >
                  {selectedTemplate === item?.template ? 'Copiado' : 'Copiar'}
                </Button>
              </div>
              <div className="p-4">
                <pre className="text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed">
                  {item?.template}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={20} />
          Consejos de Marketing
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {marketingTips?.map((tip, index) => (
            <div key={index} className="bg-gradient-to-br from-primary/5 to-secondary/5 p-4 rounded-lg border border-primary/20">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name={tip?.icon} size={20} color="white" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">{tip?.title}</h4>
                  <p className="text-xs text-muted-foreground">{tip?.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-gradient-to-r from-accent/10 to-warning/10 border border-accent/30 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="Rocket" size={24} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Maximiza tus Ganancias</h3>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span>Comparte tu enlace en múltiples plataformas para mayor alcance</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span>Crea contenido visual atractivo mostrando los beneficios de BIZZNET</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span>Responde preguntas y dudas de tus referidos para aumentar conversiones</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                <span>Celebra tus logros y comparte testimonios de éxito</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingTools;